import React, { useState, useEffect, useRef } from "react";
import { API } from "../App";
import { MessageSquare, Send, X, Sparkles } from "lucide-react";
import axios from "axios";

const STEP_MESSAGES = {
  0: "Ti guido io nella demo. In 2 minuti ti faccio vedere come SmartBooker aumenta le prenotazioni nella tua attività.",
  1: "Hai visto? Questo è il momento in cui perdi molti clienti se non rispondi subito. SmartBooker risponde in automatico, 24/7.",
  2: "Qui SmartBooker trasforma una semplice richiesta in una prenotazione confermata. Zero lavoro manuale.",
  3: "Questo è il risultato: meno buchi in agenda e più fatturato. Tutto automatico.",
  4: "SmartBooker lavora anche sui clienti che non tornano, recuperandoli con messaggi personalizzati.",
  5: "Il vero valore non è organizzare il lavoro, ma aumentare i clienti che entrano ogni mese. Vuoi attivarlo?",
};

const AISalesAssistant = ({ sessionId, currentStep, sector, language = "it" }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [hasNotif, setHasNotif] = useState(false);
  const [lastAutoStep, setLastAutoStep] = useState(-1);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const t = (it, en) => language === "it" ? it : en;

  // Auto-message on step change
  useEffect(() => {
    if (currentStep === lastAutoStep || !sessionId) return;
    const msg = STEP_MESSAGES[currentStep];
    if (!msg) return;

    setLastAutoStep(currentStep);
    const delay = currentStep === 0 ? 1500 : 800;
    const timer = setTimeout(() => {
      setMessages(prev => [...prev, { role: "assistant", text: msg }]);
      if (!open) setHasNotif(true);
    }, delay);
    return () => clearTimeout(timer);
  }, [currentStep, sessionId, lastAutoStep, open]);

  // Push conversion message after 60s
  useEffect(() => {
    if (!sessionId) return;
    const timer = setTimeout(() => {
      setMessages(prev => {
        if (prev.some(m => m.text?.includes("attività reale"))) return prev;
        return [...prev, { role: "assistant", text: t("Vuoi vedere come funzionerebbe sulla tua attività reale? Scrivimi il tuo settore!", "Want to see how it would work for your real business? Tell me your sector!") }];
      });
      if (!open) setHasNotif(true);
    }, 60000);
    return () => clearTimeout(timer);
  }, [sessionId, open, language]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open) { setHasNotif(false); inputRef.current?.focus(); }
  }, [open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    setMessages(prev => [...prev, { role: "user", text }]);
    setInput("");
    setSending(true);

    try {
      const res = await axios.post(`${API}/demo/chat/send`, {
        session_id: sessionId,
        message: text,
        step: currentStep,
        sector,
      });
      setMessages(prev => [...prev, { role: "assistant", text: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", text: t("Mi scusi, riprovi tra un momento.", "Sorry, try again in a moment.") }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-xl shadow-emerald-500/30 hover:shadow-2xl hover:shadow-emerald-500/40 hover:scale-105 active:scale-95 transition-all flex items-center justify-center"
          data-testid="ai-sales-btn"
        >
          <Sparkles className="w-6 h-6" />
          {hasNotif && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white animate-pulse">
              {messages.filter(m => m.role === "assistant").length}
            </span>
          )}
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-6rem)] rounded-2xl bg-white dark:bg-slate-900 shadow-2xl shadow-black/20 border border-slate-200 dark:border-slate-700 flex flex-col overflow-hidden" style={{ animation: "slideUp 0.3s ease-out" }} data-testid="ai-sales-panel">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-500 text-white flex-shrink-0">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm">{t("Sales Assistant", "Sales Assistant")}</p>
              <p className="text-[10px] text-emerald-100">{t("Consulente SmartBooker", "SmartBooker Consultant")}</p>
            </div>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/20 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
            {messages.length === 0 && (
              <div className="text-center py-8 text-slate-400">
                <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-xs">{t("Il tuo consulente SmartBooker", "Your SmartBooker consultant")}</p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`} style={{ animation: "fadeIn 0.25s ease-out" }}>
                <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed ${
                  msg.role === "user"
                    ? "bg-emerald-500 text-white rounded-tr-sm"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-tl-sm"
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-slate-100 dark:bg-slate-800 rounded-xl px-4 py-2.5 rounded-tl-sm">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Quick actions */}
          {messages.length <= 2 && (
            <div className="px-3 pb-2 flex gap-1.5 flex-wrap">
              {[
                t("Come funziona?", "How does it work?"),
                t("Quanto costa?", "How much?"),
                t("È adatto a me?", "Is it for me?"),
              ].map(q => (
                <button key={q} onClick={() => { setInput(q); setTimeout(() => sendMessage(), 0); setInput(q); }}
                  className="px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex items-center gap-2 p-3 border-t border-slate-200 dark:border-slate-800 flex-shrink-0">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("Scrivi una domanda...", "Ask a question...")}
              className="flex-1 h-9 px-3 rounded-full bg-slate-100 dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-emerald-500/30"
              disabled={sending}
              data-testid="ai-sales-input"
            />
            <button type="submit" disabled={!input.trim() || sending}
              className="w-9 h-9 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white flex items-center justify-center flex-shrink-0 disabled:opacity-40 transition-all"
              data-testid="ai-sales-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}

      <style>{`
        @keyframes slideUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </>
  );
};

export default AISalesAssistant;
